import { ActivitySeries } from "@common/schemas/domains/activity/activity-timeline.schema";
import { ActorType } from "@common/types/auditLog";
import type { TimelineSeriesKey } from "@components/activityTimeline/activityTimelineSeries.lib";
import { toSliceActorPicks } from "@components/activityTimeline/activityTimelinePeople.lib";
import type { HistoryFilters } from "@hooks/view/useHistoryPageFiltersCache";
import {
    isSeriesPicked,
    toIntegrationPredicates,
} from "../components/actorSeriesFilterItems";
import type { ActorFilters } from "../utils/historyFilterClears";
import { CLEARED_ACTOR_FILTERS } from "../utils/historyFilterClears";

type PickedActorFilters = Pick<
    HistoryFilters,
    "actorIds" | "actorType" | "sourcePredicates" | "series"
>;

/** The Integrations or Kick picks narrower than the whole series, which are all its slice counts; none when it's picked whole. People's come from `toSliceActorPicks`. */
function toNarrowerAgentPicks({
    series,
    filter,
}: {
    series: ActivitySeries;
    filter: PickedActorFilters;
}): Partial<ActorFilters> | undefined {
    if (isSeriesPicked({ series, filters: filter })) {
        return undefined;
    }
    switch (series) {
        case ActivitySeries.INTEGRATIONS: {
            const integrationPredicates = toIntegrationPredicates(
                filter.sourcePredicates,
            );
            return integrationPredicates.length > 0
                ? { sourcePredicates: integrationPredicates }
                : undefined;
        }
        case ActivitySeries.KICK:
            return filter.actorType === ActorType.SUPPORT
                ? { actorType: ActorType.SUPPORT }
                : undefined;
        case ActivitySeries.PEOPLE:
        case ActivitySeries.RULES:
            return undefined;
    }
}

/**
 * Updated by for a clicked slice, so the list holds exactly what the slice
 * counted: the clicked person, the picks inside its series when narrower
 * than the whole series, or else the whole series. Picks outside it are
 * dropped.
 */
export function toSliceActorFilters({
    key,
    filter,
}: {
    key: TimelineSeriesKey;
    filter: PickedActorFilters;
}): ActorFilters {
    const agentPicks =
        key.personId === undefined
            ? toNarrowerAgentPicks({ series: key.series, filter })
            : undefined;
    return {
        ...CLEARED_ACTOR_FILTERS,
        ...(agentPicks ?? toSliceActorPicks({ key, filter })),
    };
}
